import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';
import { loginWithCustomToken, logoutUser } from '@/services/firebase.service';
import Utils from '@/utils';
import { getSuarteEnvironment } from '@/utils/environment.utils';

function login(email, password, turnstileToken) {
  return fetchWrapper.post(`${config.apis.api.url}/login/custom`, {
    body: {
      user_email: email.toLowerCase(),
      user_password: password,
      ...(turnstileToken ? { "cf-turnstile-response": turnstileToken } : {})
    }
  });
}

function logout() {
  logoutUser()
    .catch((error) => {
      console.error("[AuthService] logout failed", error);
    })
    .finally(() => {
      try {
        const env = getSuarteEnvironment();
        if (env.isNativeWrapper && typeof window !== "undefined") {
          window.location.href = "logout://";
        }
      } catch (err) {
        console.error("[AuthService] failed to trigger native logout", err);
      }
    });
}

function registerCollector(email, password, invite) {
  return fetchWrapper.post(`${config.apis.api.url}/register`, {
    body: {
      user_email: email.toLowerCase(),
      user_password: password,
      user_type: "collector",
      invite
    }
  });
}

function requestResetPassword(email) {
  return fetchWrapper.post(`${config.apis.api.url}/forgot-password`, {
    body: {
      user_email: email.toLowerCase()
    }
  });
}

function resetPassword(password, oobCode, uid) {
  return fetchWrapper.post(`${config.apis.api.url}/reset-password`, {
    body: {
      user_password: password,
      oobCode: oobCode,
      uid: uid,
    }
  });
}

function getCustomToken(firebaseAccessToken, invite) {
  return fetchWrapper.post(`${config.apis.api.url}/login/google`, {
    body: {
      access_token: firebaseAccessToken,
      invite
    }
  });
}

function redeemSuarteFrontendTicket(ticket) {
  return fetchWrapper.post(`${config.apis.api.url}/auth/cross-app/suarte-frontend-ticket/redeem`, {
    body: { ticket },
  });
}

function registerGallery(galleryData) {
  return fetchWrapper.post(`${config.apis.api.url}/request/access/gallery`, {
    body: {
      user_name: galleryData.user_name.value,
      user_username: galleryData.user_username.value,
      user_email: galleryData.user_email.value.toLowerCase(),
      gallery_business_id: galleryData.gallery_business_id.value,
      gallery_address: {
        street: galleryData["gallery_address.street"].value,
        city: galleryData["gallery_address.city"].value,
        zip_code: galleryData["gallery_address.zip_code"].value, 
        region: galleryData["gallery_address.region"].value,
        country: galleryData["gallery_address.country"].value
      },
      user_password: galleryData.user_password.value,
      gallery_agent: {
        agent_name: galleryData["gallery_agent.agent_name"].value,
        agent_gender: galleryData["gallery_agent.agent_gender"].value,
        agent_birth: Utils.getYearObjectFromInput(galleryData["gallery_agent.agent_birth"].value),
        agent_phone: {
          phone_prefix: galleryData["gallery_agent.agent_phone.phone_prefix"].value,
          phone_number: galleryData["gallery_agent.agent_phone.phone_number"].value,
        }
      },
      gallery_comment: galleryData.gallery_comment.value,
    }
  });
}

function claimArtistProfile(email) {
  return fetchWrapper.post(`${config.apis.api.url}/request/access/artist`, {
    body: {
      user_email: email.toLowerCase()
    }
  });
}

function resendArtistCode(userEmail, oobCode) {
  return fetchWrapper.post(`${config.apis.api.url}/request/access/artist/resend`, {
    body: {
      oobCode,
      user_email: userEmail
    }
  });
}

function verifyArtistCode(code, oobCode) {
  return fetchWrapper.post(`${config.apis.api.url}/request/access/artist/verify`, {
    body: {
      code: code,
      oobCode: oobCode
    }
  });
}

function registerArtist(oobCode, password, invite) {
  return fetchWrapper.post(`${config.apis.api.url}/request/access/artist/confirm`, {
    body: {
      oobCode: oobCode,
      user_password: password,
      invite
    }
  });
}

async function initUserSession(stateHandler, setLoading, data) {
  await loginWithCustomToken(data.custom_token);
  stateHandler.set("user_session", data.user_session);
  if(setLoading) setLoading(false);
}

const AuthService = {
  login,
  logout,
  registerCollector,
  requestResetPassword,
  resetPassword,
  getCustomToken,
  redeemSuarteFrontendTicket,
  registerGallery,
  claimArtistProfile,
  resendArtistCode,
  verifyArtistCode,
  registerArtist,
  initUserSession
};

export default AuthService;
